<template>
  <div class="gesp-mock-container">
    <div class="gesp-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="handleBack"></i>
        <h1 class="page-title">{{ currentView === 'practice' ? 'GESP 模拟测试' : 'GESP 模拟考试' }}</h1>
      </div>
      <div class="header-right">
        <template v-if="currentView === 'practice' && !loading">
          <div class="timer-bar" v-if="!isReviewMode">
            <i class="el-icon-time"></i>
            <span class="timer-display">{{ formattedElapsed }}</span>
          </div>
          <el-tag v-if="isReviewMode" type="success" size="small">已完成</el-tag>
        </template>
      </div>
    </div>

    <!-- ========== 首页：选择模式 ========== -->
    <div v-if="currentView === 'home'" class="home-view">
      <!-- 模式切换 Tabs -->
      <div class="mode-tabs">
        <div class="mode-tab" :class="{ active: activeMode === 'preset' }" @click="activeMode = 'preset'">
          <i class="el-icon-document-copy"></i> 模拟卷
        </div>
        <div class="mode-tab" :class="{ active: activeMode === 'custom' }" @click="activeMode = 'custom'">
          <i class="el-icon-magic-stick"></i> 自行组卷
        </div>
      </div>

      <!-- ===== 模拟卷模式 ===== -->
      <div v-if="activeMode === 'preset'" class="mode-content">
        <div v-if="loadingList" class="loading-container"><i class="el-icon-loading"></i> 加载中...</div>
        <div v-else-if="presetPapers.length === 0" class="empty-hint">
          <i class="el-icon-folder-opened" style="font-size:48px;color:#ccc;"></i>
          <p>暂无预设模拟卷</p>
        </div>
        <div v-else class="paper-grid">
          <div v-for="p in presetPapers" :key="p.id" class="paper-card" @click="openPaperInNewTab(p)">
            <div class="paper-card-icon"><i class="el-icon-notebook-2"></i></div>
            <div class="paper-card-info">
              <div class="paper-card-title">{{ p.title }}</div>
              <div class="paper-card-meta">{{ p.totalQuestions || '?' }} 题</div>
            </div>
            <el-button type="primary" size="mini" class="paper-card-btn">开始</el-button>
          </div>
        </div>
      </div>

      <!-- ===== 自行组卷模式 ===== -->
      <div v-if="activeMode === 'custom'" class="mode-content">
        <!-- 组卷配置表单 -->
        <div class="custom-form-card">
          <h3 class="form-title"><i class="el-icon-setting"></i> 组卷设置</h3>
          <el-form label-width="100px" size="small">
            <el-form-item label="题型分布">
              <div class="diff-sliders">
                <div class="diff-row">
                  <span class="diff-label judge-label">判断题</span>
                  <el-slider v-model="paperConfig.judgeCount" :min="0" :max="30" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ paperConfig.judgeCount }}</span>
                </div>
                <div class="diff-row">
                  <span class="diff-label choice-label">单选题</span>
                  <el-slider v-model="paperConfig.choiceCount" :min="0" :max="30" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ paperConfig.choiceCount }}</span>
                </div>
                <div class="diff-row">
                  <span class="diff-label code-label">编程题</span>
                  <el-slider v-model="paperConfig.codeCount" :min="0" :max="10" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ paperConfig.codeCount }}</span>
                </div>
              </div>
              <div class="diff-tip">默认按 GESP 考试规则预设为 10 / 15 / 2，你可以自行调整；总计 <strong>{{ paperConfig.judgeCount + paperConfig.choiceCount + paperConfig.codeCount }}</strong> 题。</div>
              <div class="diff-actions">
                <el-button size="mini" @click="resetPaperConfig">恢复默认分布</el-button>
              </div>
            </el-form-item>
            <el-form-item label="编程题设置">
              <el-switch
                v-model="paperConfig.includeSubmittedCode"
                active-text="包含已提交题目"
                inactive-text="排除已提交题目"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-magic-stick" :loading="generating" @click="generatePaper">立即组卷</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 用户历史组卷记录 -->
        <div class="history-section">
          <h3 class="section-title"><i class="el-icon-time"></i> 我的组卷记录</h3>
          <div v-if="loadingList" class="loading-container"><i class="el-icon-loading"></i> 加载中...</div>
          <div v-else-if="userPapers.length === 0" class="empty-hint">
            <p>暂无组卷记录，试试立即组一套吧！</p>
          </div>
          <div v-else class="paper-grid">
            <div v-for="p in userPapers" :key="p.id" class="paper-card" :class="{ completed: p.status === 'completed' }">
              <div class="paper-card-icon">
                <i :class="p.status === 'completed' ? 'el-icon-check' : 'el-icon-document'"></i>
              </div>
              <div class="paper-card-info">
                <div class="paper-card-title">{{ p.title }}</div>
                <div class="paper-card-meta">
                  {{ p.totalQuestions || '?' }} 题
                  <span v-if="p.status === 'completed'" class="score-badge">得分 {{ p.score != null ? p.score : '-' }}</span>
                  <span v-if="getPaperElapsedSeconds(p) != null" class="elapsed-badge">耗时 {{ formatElapsedForCard(getPaperElapsedSeconds(p)) }}</span>
                </div>
                <div class="paper-card-time">{{ formatTime(p.createdAt) }}</div>
              </div>
              <div class="paper-card-actions">
                <el-button :type="p.status === 'completed' ? 'success' : 'primary'" size="mini" @click.stop="openPaperInNewTab(p)">
                  {{ p.status === 'completed' ? '查看' : '做题' }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== 做题视图 ========== -->
    <div v-if="currentView === 'practice'" class="practice-view">
      <div v-if="loading" style="text-align:center; padding:60px; color:#999;"><i class="el-icon-loading"></i> 正在加载试卷...</div>
      <div v-else-if="error" style="text-align:center; padding:60px; color:#F56C6C;">{{ error }}</div>

      <div v-else class="gesp-body">
        <!-- 左侧面板 -->
        <div class="left-panel">
          <div class="panel-title">题目总览</div>
          <div class="type-menu">
            <div
              v-for="t in visibleQuestionTypes"
              :key="t.key"
              class="type-item"
              :class="{ active: activeType === t.key }"
              @click="switchType(t.key)"
            >
              <span class="type-name">{{ t.label }}</span>
              <span class="type-count">{{ t.done }}/{{ t.total }}</span>
            </div>
          </div>

          <div class="number-panel">
            <div class="number-title">
              <span>{{ activeTypeLabel }}</span>
              <span class="number-meta">
                <el-checkbox v-model="showLegend" size="mini">图例</el-checkbox>
              </span>
            </div>
            <div class="legend" v-if="showLegend">
              <div class="legend-item"><span class="dot done"></span>{{ submitted ? '答对' : '已作答' }}</div>
              <div class="legend-item"><span class="dot todo"></span>未作答</div>
              <div class="legend-item"><span class="dot wrong"></span>{{ submitted ? '答错' : '已作答' }}</div>
              <div class="legend-item" v-if="!submitted"><span class="dot answered"></span>已选择</div>
            </div>
            <div class="number-grid">
              <div
                v-for="n in activeNumbers"
                :key="n.no"
                class="num"
                :class="n.status"
                @click="selectQuestion(n.no)"
              >
                {{ n.no }}
              </div>
            </div>
          </div>

          <div style="margin-top: 16px; padding: 0 4px;">
            <el-button v-if="!submitted && !isReviewMode" type="danger" size="small" style="width: 100%;" @click="submitAll">提交全部</el-button>
            <el-button type="default" size="small" style="width: 100%; margin-top: 8px;" @click="backToHome">返回列表</el-button>
          </div>
        </div>

        <!-- 右侧面板 -->
        <div class="right-panel">
          <!-- 选择题 / 判断题 -->
          <div class="question-card" v-if="activeType !== 'code'">
            <div class="q-header">
              <div class="q-title">
                <span class="q-id">第 {{ currentNo }} 题 / 共 {{ (questionTypes.find(t => t.key === activeType) || {}).total || 0 }} 题</span>
                <el-tag size="mini" type="success" v-if="activeType === 'judge'">判断题</el-tag>
                <el-tag size="mini" type="primary" v-else>单选题</el-tag>
              </div>
              <div class="q-meta">分值 {{ currentQuestion.score }} 分</div>
            </div>

            <div class="q-content" v-html="currentQuestion.content"></div>

            <div v-if="currentQuestionImages.length > 0" class="q-images">
              <el-image
                v-for="(img, idx) in currentQuestionImages"
                :key="`${currentQuestion.id || currentNo}-${idx}`"
                class="q-image"
                :src="img"
                :preview-src-list="currentQuestionImages"
                fit="contain"
              />
            </div>

            <div class="q-options" v-if="activeType === 'choice'">
              <el-radio-group v-model="answers.choice[currentNo]" :disabled="submitted || isReviewMode" @change="onChoiceAnswerChange">
                <el-radio v-for="(opt, idx) in currentQuestion.options" :key="opt.key" :label="opt.key" class="opt">
                  <template v-if="getOptionImage(currentQuestion, idx)">
                    <div class="opt-image-wrap">
                      <span class="opt-key">{{ opt.key }}.</span>
                      <el-image
                        :src="getOptionImage(currentQuestion, idx)"
                        :preview-src-list="[getOptionImage(currentQuestion, idx)]"
                        fit="contain"
                        class="opt-image"
                      />
                    </div>
                  </template>
                  <template v-else>
                    {{ opt.key }}. {{ opt.value }}
                  </template>
                </el-radio>
              </el-radio-group>
            </div>

            <div class="q-options" v-else>
              <el-radio-group v-model="answers.judge[currentNo]" :disabled="submitted || isReviewMode" @change="onJudgeAnswerChange">
                <el-radio label="A" class="opt">正确</el-radio>
                <el-radio label="B" class="opt">错误</el-radio>
              </el-radio-group>
            </div>

            <div class="q-result" v-if="submitted && currentResult">
              <div class="result-row">
                <span class="result-label">评测结果</span>
                <span class="result-value" :class="currentResult.isCorrect ? 'correct' : 'wrong'">
                  {{ currentResult.isCorrect ? '✓ 正确' : '✗ 错误' }}
                </span>
              </div>
              <div class="result-row" v-if="!currentResult.isCorrect">
                <span class="result-label">正确答案</span>
                <span class="result-value correct">{{ currentResult.correctAnswer }}</span>
              </div>
            </div>

            <div class="q-actions">
              <el-button size="small" @click="prev">上一题</el-button>
              <el-button size="small" @click="next">下一题</el-button>
            </div>
          </div>

          <!-- 编程题列表 -->
          <div class="question-card" v-else-if="activeType === 'code' && codeQuestions.length > 0">
            <div class="q-header">
              <div class="q-title">
                <el-tag size="mini" type="warning">编程题</el-tag>
                <span style="margin-left: 8px; color: #666; font-size: 13px;">共 {{ codeQuestions.length }} 题，点击进入作答</span>
              </div>
            </div>
            <div v-for="(q, idx) in codeQuestions" :key="q.id" class="code-list-item" :class="{ completed: !!(q && q.hasSubmitted) }">
              <div class="code-list-left">
                <span class="code-list-no">{{ idx + 1 }}</span>
                <div class="code-list-info">
                  <div class="code-list-number">{{ q.number }}</div>
                  <div class="code-list-title">{{ q.title }}</div>
                </div>
              </div>
              <div class="code-list-right">
                <el-tag v-if="q.difficulty" size="mini" :type="difficultyTagType(q.difficulty)" style="margin-right: 8px;">Level {{ q.difficulty }}</el-tag>
                <el-button type="primary" size="mini" @click="goToCodeDetail(q)">进入作答</el-button>
              </div>
            </div>
          </div>

          <div class="question-card" v-else-if="activeType === 'code'">
            <div style="text-align:center; color: #999; padding: 40px 0;">本次模拟暂无编程题</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 提交结果弹窗 -->
    <el-dialog
      title="模拟测试结果"
      :visible.sync="submitResultVisible"
      width="400px"
      :close-on-click-modal="false"
      center
    >
      <div v-if="submitResultData" style="text-align: center; padding: 10px 0;">
        <div style="font-size: 48px; font-weight: 700; color: #409EFF; line-height: 1.2;">
          {{ submitResultData.rightCount }}<span style="font-size: 24px; color: #999;"> / {{ submitResultData.totalAnswered }}</span>
        </div>
        <div style="color: #999; font-size: 13px; margin: 6px 0 20px;">本次已作答题目正确数</div>
        <div style="display: flex; justify-content: center; gap: 32px; margin-bottom: 16px;">
          <div style="text-align: center;">
            <div style="font-size: 22px; font-weight: 700; color: #67C23A;">{{ submitResultData.rightCount }}</div>
            <div style="font-size: 12px; color: #999;">答对</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 22px; font-weight: 700; color: #F56C6C;">{{ submitResultData.wrongCount }}</div>
            <div style="font-size: 12px; color: #999;">答错</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 22px; font-weight: 700; color: #E6A23C;">{{ submitResultData.totalQuestions - submitResultData.totalAnswered }}</div>
            <div style="font-size: 12px; color: #999;">未作答</div>
          </div>
        </div>
        <div style="background: #f5f7fa; border-radius: 8px; padding: 10px; color: #555; font-size: 14px;">
          正确率：<strong style="color: #409EFF;">{{ submitResultData.totalAnswered > 0 ? Math.round(submitResultData.rightCount / submitResultData.totalAnswered * 100) : 0 }}%</strong>
        </div>
      </div>
      <div slot="footer">
        <el-button type="primary" @click="closeAndReturn">返回原页面</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listPresetPapers, listUserPapers, generateGespPaper, getGespPaperDetail, savePaperProgress, submitPaperResult } from '@/api/mockTest'

export default {
  name: 'GESPMockTest',
  data() {
    return {
      currentView: 'home',
      // 首页
      activeMode: 'preset',
      loadingList: false,
      presetPapers: [],
      userPapers: [],
      generating: false,
      paperConfig: { judgeCount: 10, choiceCount: 15, codeCount: 2, includeSubmittedCode: false },
      // 做题
      loading: false,
      error: null,
      repoId: null,
      codeRepoId: 27,
      level: null,
      currentPaperId: null,
      currentPaper: null,
      showLegend: true,
      activeType: 'judge',
      currentNo: 1,
      questionTypes: [
        { key: 'judge', label: '判断题', done: 0, total: 0 },
        { key: 'choice', label: '单选题', done: 0, total: 0 },
        { key: 'code', label: '编程题', done: 0, total: 0 }
      ],
      choiceQuestions: [],
      judgeQuestions: [],
      codeQuestions: [],
      answers: { choice: {}, judge: {} },
      numberStatus: { choice: {}, judge: {} },
      questionResults: {},
      submitted: false,
      submitResultVisible: false,
      submitResultData: null,
      // 计时
      elapsedSeconds: 0,
      timer: null
    }
  },
  computed: {
    isReviewMode() {
      return this.currentPaper && this.currentPaper.status === 'completed'
    },
    formattedElapsed() {
      const t = this.elapsedSeconds || 0
      const mm = String(Math.floor(t / 60)).padStart(2, '0')
      const ss = String(t % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
    visibleQuestionTypes() {
      return (this.questionTypes || []).filter(t => Number(t && t.total) > 0)
    },
    activeTypeLabel() {
      const t = this.visibleQuestionTypes.find(x => x.key === this.activeType) || this.questionTypes.find(x => x.key === this.activeType)
      return t ? t.label : ''
    },
    currentResult() {
      if (!this.submitted) return null
      return this.questionResults[`${this.activeType}_${this.currentNo}`] || null
    },
    activeNumbers() {
      if (this.activeType === 'code') {
        return this.codeQuestions.map((q, i) => ({
          no: i + 1,
          status: q && q.hasSubmitted ? 'done' : 'todo'
        }))
      }
      const total = (this.questionTypes.find(x => x.key === this.activeType) || {}).total || 0
      const statusMap = this.numberStatus[this.activeType] || {}
      return Array.from({ length: total }, (_, i) => ({
        no: i + 1,
        status: statusMap[i + 1] || 'todo'
      }))
    },
    currentQuestion() {
      const questions = this.activeType === 'choice' ? this.choiceQuestions : this.judgeQuestions
      const q = questions[this.currentNo - 1]
      return q ? this.formatQuestion(q) : { id: null, score: 0, content: '', options: [], answer: '' }
    },
    currentQuestionImages() {
      const questions = this.activeType === 'choice' ? this.choiceQuestions : this.judgeQuestions
      const q = questions[this.currentNo - 1]
      return this.getQuestionImages(q)
    }
  },
  watch: {
    'answers.choice': {
      deep: true,
      handler(val) {
        if (this.submitted) return
        Object.keys(val).forEach(no => {
          if (val[no]) this.$set(this.numberStatus.choice, Number(no), 'answered')
        })
        const t = this.questionTypes.find(x => x.key === 'choice')
        if (t) t.done = Object.keys(val).filter(k => val[k]).length
        this.saveDraftState()
      }
    },
    'answers.judge': {
      deep: true,
      handler(val) {
        if (this.submitted) return
        Object.keys(val).forEach(no => {
          if (val[no]) this.$set(this.numberStatus.judge, Number(no), 'answered')
        })
        const t = this.questionTypes.find(x => x.key === 'judge')
        if (t) t.done = Object.keys(val).filter(k => val[k]).length
        this.saveDraftState()
      }
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.repoId = this.$route.query.repoId
    this.level = this.$route.query.level ? Number(this.$route.query.level) : null
    this.codeRepoId = this.$route.query.codeRepoId ? Number(this.$route.query.codeRepoId) : 27
    if (!this.repoId) {
      this.$message.error('缺少题库ID参数')
      this.$router.go(-1)
      return
    }
    this.loadPaperList().then(() => {
      const paperId = this.$route.query.paperId ? Number(this.$route.query.paperId) : null
      if (!paperId) return
      const papers = [...(this.userPapers || []), ...(this.presetPapers || [])]
      const matched = papers.find(item => Number(item && item.id) === paperId)
      if (matched) {
        this.enterPaper(matched)
      }
    })
  },
  beforeDestroy() {
    this.stopTimer()
    this.showSidebarAndNavbar()
  },
  methods: {
    getDraftStorageKey(paperId) {
      const id = paperId || this.currentPaperId
      return id ? `gesp_mock_test_draft_${id}` : ''
    },
    saveDraftState() {
      if (!this.currentPaperId || this.submitted || this.isReviewMode) return
      const key = this.getDraftStorageKey()
      if (!key) return
      const payload = {
        answers: this.answers,
        numberStatus: this.numberStatus,
        questionResults: this.questionResults,
        activeType: this.activeType,
        currentNo: this.currentNo,
        elapsedSeconds: this.elapsedSeconds,
        submitResultData: this.submitResultData,
        showLegend: this.showLegend,
        savedAt: Date.now()
      }
      try {
        window.sessionStorage.setItem(key, JSON.stringify(payload))
      } catch (e) {
        console.warn('保存模拟测试草稿失败:', e)
      }
    },
    getDraftState(paperId) {
      const key = this.getDraftStorageKey(paperId)
      if (!key) return null
      try {
        const raw = window.sessionStorage.getItem(key)
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        console.warn('读取模拟测试草稿失败:', e)
        return null
      }
    },
    clearDraftState(paperId) {
      const key = this.getDraftStorageKey(paperId)
      if (!key) return
      try {
        window.sessionStorage.removeItem(key)
      } catch (e) {
        console.warn('清理模拟测试草稿失败:', e)
      }
    },
    ensureActiveTypeValid() {
      const visible = this.visibleQuestionTypes || []
      if (!visible.length) {
        this.activeType = 'judge'
        this.currentNo = 1
        return
      }
      const exists = visible.some(t => t.key === this.activeType)
      if (!exists) {
        this.activeType = visible[0].key
        this.currentNo = 1
      }
    },
    restoreDraftState(draft) {
      if (!draft || this.submitted || this.isReviewMode) return
      this.answers = draft.answers || { choice: {}, judge: {} }
      this.numberStatus = draft.numberStatus || { choice: {}, judge: {} }
      this.questionResults = draft.questionResults || {}
      this.activeType = draft.activeType || this.activeType || 'judge'
      this.currentNo = draft.currentNo || 1
      this.elapsedSeconds = Number(draft.elapsedSeconds || 0)
      this.submitResultData = draft.submitResultData || null
      this.showLegend = typeof draft.showLegend === 'boolean' ? draft.showLegend : this.showLegend
      const judgeType = this.questionTypes.find(t => t.key === 'judge')
      const choiceType = this.questionTypes.find(t => t.key === 'choice')
      const codeType = this.questionTypes.find(t => t.key === 'code')
      if (judgeType) judgeType.done = Object.keys(this.answers.judge || {}).filter(k => this.answers.judge[k]).length
      if (choiceType) choiceType.done = Object.keys(this.answers.choice || {}).filter(k => this.answers.choice[k]).length
      if (codeType) codeType.done = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length
      this.ensureActiveTypeValid()
    },
    buildAnswerDetailMap() {
      const detail = {
        judge: {},
        choice: {},
        code: {},
        meta: {
          elapsedSeconds: Number(this.elapsedSeconds || 0)
        }
      }

      this.judgeQuestions.forEach((q, idx) => {
        const no = idx + 1
        const userAnswer = this.answers.judge[no]
        if (!userAnswer) return
        const existing = this.questionResults[`judge_${no}`] || {}
        const correctAnswer = existing.correctAnswer || this.normalizeJudgeAnswer(q.answer)
        const hasJudged = typeof existing.isCorrect === 'boolean'
        detail.judge[no] = {
          userAnswer,
          correctAnswer,
          isCorrect: hasJudged ? existing.isCorrect : null,
          score: existing.score != null ? existing.score : 0
        }
      })

      this.choiceQuestions.forEach((q, idx) => {
        const no = idx + 1
        const userAnswer = this.answers.choice[no]
        if (!userAnswer) return
        const existing = this.questionResults[`choice_${no}`] || {}
        const correctAnswer = existing.correctAnswer || this.extractChoiceKey(q.answer)
        const hasJudged = typeof existing.isCorrect === 'boolean'
        detail.choice[no] = {
          userAnswer,
          correctAnswer,
          isCorrect: hasJudged ? existing.isCorrect : null,
          score: existing.score != null ? existing.score : 0
        }
      })

      this.codeQuestions.forEach((q, idx) => {
        const no = idx + 1
        if (!q || !q.hasSubmitted) return
        detail.code[no] = {
          questionId: q.id,
          status: q.latestSubmitStatus || 'AC',
          isCorrect: String(q.latestSubmitStatus || '').toUpperCase() === 'AC',
          passedCount: Number(q.passedCount || 0),
          totalCount: Number(q.totalCount || 0)
        }
      })

      return detail
    },
    getElapsedSecondsFromAnswerDetail(detail) {
      if (!detail) return null
      let parsed = detail
      if (typeof detail === 'string') {
        try {
          parsed = JSON.parse(detail)
        } catch (e) {
          return null
        }
      }
      const elapsed = parsed && parsed.meta ? Number(parsed.meta.elapsedSeconds) : NaN
      return Number.isFinite(elapsed) ? elapsed : null
    },
    async persistPaperProgress(extraDetail) {
      if (!this.currentPaperId || this.submitted || this.isReviewMode) return
      try {
        const answerDetailMap = this.buildAnswerDetailMap()
        if (extraDetail && typeof extraDetail === 'object') {
          if (extraDetail.judge) answerDetailMap.judge = { ...answerDetailMap.judge, ...extraDetail.judge }
          if (extraDetail.choice) answerDetailMap.choice = { ...answerDetailMap.choice, ...extraDetail.choice }
          if (extraDetail.code) answerDetailMap.code = { ...answerDetailMap.code, ...extraDetail.code }
          if (extraDetail.meta) answerDetailMap.meta = { ...answerDetailMap.meta, ...extraDetail.meta }
        }
        const judgeCorrect = Object.values(answerDetailMap.judge || {}).filter(item => item && item.isCorrect === true).length
        const choiceCorrect = Object.values(answerDetailMap.choice || {}).filter(item => item && item.isCorrect === true).length
        const codePassedCount = Object.values(answerDetailMap.code || {}).reduce((sum, item) => sum + (Number(item && item.passedCount) || 0), 0)
        const objectiveCorrectCount = judgeCorrect + choiceCorrect
        const totalCorrectCount = objectiveCorrectCount + codePassedCount
        const score = (objectiveCorrectCount * 5) + (codePassedCount * 5)
        await savePaperProgress(this.currentPaperId, {
          score,
          correctCount: totalCorrectCount,
          answerDetail: JSON.stringify(answerDetailMap)
        })
        if (this.currentPaper) {
          this.currentPaper = {
            ...this.currentPaper,
            answerDetail: JSON.stringify(answerDetailMap),
            correctCount: totalCorrectCount,
            score,
            status: this.currentPaper.status === 'completed' ? 'completed' : 'started'
          }
        }
      } catch (e) {
        console.warn('保存试卷进度失败:', e)
      }
    },
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })
      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')
      if (sidebar) sidebar.style.display = 'none'
      if (navbar) navbar.style.display = 'none'
      const appMain = document.querySelector('.app-main')
      if (appMain) { appMain.style.padding = '0'; appMain.style.margin = '0' }
    },
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })
      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')
      if (sidebar) sidebar.style.display = ''
      if (navbar) navbar.style.display = ''
      const appMain = document.querySelector('.app-main')
      if (appMain) { appMain.style.padding = ''; appMain.style.margin = '' }
    },

    // ========== 首页 ==========
    async loadPaperList() {
      this.loadingList = true
      try {
        // 加载预设模拟卷
        try {
          const presetRes = await listPresetPapers(this.repoId)
          const presetData = presetRes?.data?.data || presetRes?.data || []
          this.presetPapers = (Array.isArray(presetData) ? presetData : []).filter(p => {
            const ct = (p.courseType || '').toUpperCase()
            return ct.includes('GESP')
          })
        } catch (e) {
          console.warn('加载预设模拟卷失败:', e)
        }
        // 加载用户自组卷
        const res = await listUserPapers(this.repoId)
        const data = res?.data?.data || res?.data || []
        this.userPapers = (Array.isArray(data) ? data : []).filter(p => {
          const ct = (p.courseType || '').toUpperCase()
          return ct.includes('GESP')
        })
      } catch (e) {
        console.warn('加载试卷列表失败:', e)
      } finally {
        this.loadingList = false
      }
    },

    resetPaperConfig() {
      this.paperConfig = { judgeCount: 10, choiceCount: 15, codeCount: 2, includeSubmittedCode: false }
    },

    async generatePaper() {
      this.generating = true
      try {
        const { judgeCount, choiceCount, codeCount, includeSubmittedCode } = this.paperConfig
        if (judgeCount + choiceCount + codeCount === 0) {
          this.$message.warning('请至少设置一种题型的数量')
          this.generating = false
          return
        }
        const res = await generateGespPaper({
          repoId: Number(this.repoId),
          codeRepoId: this.codeRepoId,
          level: this.level,
          judgeCount,
          choiceCount,
          codeCount,
          includeSubmittedCode
        })
        const paper = res?.data?.data || res?.data
        if (paper && paper.id) {
          this.$message.success('组卷成功')
          await this.loadPaperList()
          this.openPaperInNewTab(paper)
        } else {
          this.$message.error('组卷失败')
        }
      } catch (e) {
        console.error('组卷失败:', e)
        this.$message.error('组卷失败，请稍后重试')
      } finally {
        this.generating = false
      }
    },

    openPaperInNewTab(paper) {
      if (!paper || !paper.id) {
        this.$message.error('试卷信息不存在')
        return
      }
      const routeData = this.$router.resolve({
        path: '/gesp-mock-test',
        query: {
          repoId: this.repoId || undefined,
          level: this.level || undefined,
          codeRepoId: this.codeRepoId || undefined,
          paperId: paper.id
        }
      })
      window.open(routeData.href, '_blank')
    },

    async enterPaper(paper) {
      this.currentPaper = paper
      this.currentPaperId = paper.id
      this.currentView = 'practice'
      this.submitted = false
      this.questionResults = {}
      this.answers = { choice: {}, judge: {} }
      this.numberStatus = { choice: {}, judge: {} }
      this.activeType = 'judge'
      this.currentNo = 1
      await this.loadPaperDetail()
    },

    async loadPaperDetail() {
      this.loading = true
      this.error = null
      try {
        const res = await getGespPaperDetail(this.currentPaperId)
        const data = res?.data?.data || res?.data || {}
        this.currentPaper = data.paper || this.currentPaper

        this.judgeQuestions = Array.isArray(data.judgeQuestions) ? data.judgeQuestions : []
        this.choiceQuestions = Array.isArray(data.choiceQuestions) ? data.choiceQuestions : []
        this.codeQuestions = Array.isArray(data.codeQuestions) ? data.codeQuestions : []

        const judgeType = this.questionTypes.find(t => t.key === 'judge')
        const choiceType = this.questionTypes.find(t => t.key === 'choice')
        const codeType = this.questionTypes.find(t => t.key === 'code')
        if (judgeType) judgeType.total = this.judgeQuestions.length
        if (choiceType) choiceType.total = this.choiceQuestions.length
        if (codeType) codeType.total = this.codeQuestions.length

        const draft = !this.isReviewMode ? this.getDraftState(this.currentPaperId) : null

        if (this.isReviewMode) {
          this.submitted = true
          this.clearDraftState(this.currentPaperId)
          this.restoreAnswerDetail()
        } else {
          this.restoreAnswerDetail()
          if (!this.currentPaper?.answerDetail && draft) {
            this.restoreDraftState(draft)
          }
          this.resetTimer()
          const elapsedFromDetail = this.getElapsedSecondsFromAnswerDetail(this.currentPaper && this.currentPaper.answerDetail)
          this.elapsedSeconds = elapsedFromDetail != null ? elapsedFromDetail : (draft ? Number(draft.elapsedSeconds || 0) : 0)
          this.startTimer()
        }

        // 默认切到有题目的类型
        if (!draft) {
          if (this.judgeQuestions.length > 0) {
            this.activeType = 'judge'
          } else if (this.choiceQuestions.length > 0) {
            this.activeType = 'choice'
          } else {
            this.activeType = 'code'
          }
        }
        this.ensureActiveTypeValid()
      } catch (e) {
        console.error('加载试卷失败:', e)
        this.error = '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    restoreAnswerDetail() {
      if (!this.currentPaper || !this.currentPaper.answerDetail) return
      try {
        const detail = JSON.parse(this.currentPaper.answerDetail)
        if (!detail) return
        if (detail.judge) {
          Object.keys(detail.judge).forEach(no => {
            const d = detail.judge[no]
            this.$set(this.answers.judge, Number(no), d.userAnswer || '')
            const status = typeof d.isCorrect === 'boolean' ? (d.isCorrect ? 'done' : 'wrong') : 'answered'
            this.$set(this.numberStatus.judge, Number(no), status)
            if (typeof d.isCorrect === 'boolean') {
              this.$set(this.questionResults, `judge_${no}`, d)
            }
          })
        }
        if (detail.choice) {
          Object.keys(detail.choice).forEach(no => {
            const d = detail.choice[no]
            this.$set(this.answers.choice, Number(no), d.userAnswer || '')
            const status = typeof d.isCorrect === 'boolean' ? (d.isCorrect ? 'done' : 'wrong') : 'answered'
            this.$set(this.numberStatus.choice, Number(no), status)
            if (typeof d.isCorrect === 'boolean') {
              this.$set(this.questionResults, `choice_${no}`, d)
            }
          })
        }
        if (detail.code) {
          this.codeQuestions = (this.codeQuestions || []).map((q, idx) => {
            const d = detail.code[String(idx + 1)] || detail.code[idx + 1]
            if (!d) return q
            return {
              ...q,
              hasSubmitted: true,
              latestSubmitStatus: d.status || (d.isCorrect ? 'AC' : 'WA'),
              passedCount: Number(d.passedCount || 0),
              totalCount: Number(d.totalCount || 0)
            }
          })
        }
        const elapsedFromDetail = this.getElapsedSecondsFromAnswerDetail(detail)
        if (elapsedFromDetail != null) {
          this.elapsedSeconds = elapsedFromDetail
        }
        const judgeType = this.questionTypes.find(t => t.key === 'judge')
        const choiceType = this.questionTypes.find(t => t.key === 'choice')
        const codeType = this.questionTypes.find(t => t.key === 'code')
        if (judgeType) judgeType.done = Object.keys(this.numberStatus.judge).filter(k => this.numberStatus.judge[k]).length
        if (choiceType) choiceType.done = Object.keys(this.numberStatus.choice).filter(k => this.numberStatus.choice[k]).length
        if (codeType) codeType.done = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length
        this.ensureActiveTypeValid()
      } catch (e) {
        console.warn('恢复答题记录失败:', e)
      }
    },

    backToHome() {
      this.stopTimer()
      this.clearDraftState(this.currentPaperId)
      const closed = this.closeCurrentWindow()
      if (!closed) {
        this.currentView = 'home'
        this.loadPaperList()
        this.$router.go(-1)
      }
    },

    handleBack() {
      if (this.currentView === 'practice') {
        this.backToHome()
      } else {
        const closed = this.closeCurrentWindow()
        if (!closed) {
          this.$router.go(-1)
        }
      }
    },

    // ========== 做题逻辑 ==========
    onChoiceAnswerChange(value) {
      if (this.submitted || this.isReviewMode) return
      this.$set(this.answers.choice, this.currentNo, value)
      this.$set(this.numberStatus.choice, this.currentNo, value ? 'answered' : 'todo')
      const t = this.questionTypes.find(x => x.key === 'choice')
      if (t) t.done = Object.keys(this.answers.choice || {}).filter(k => this.answers.choice[k]).length
      this.saveDraftState()
      this.persistPaperProgress()
    },
    onJudgeAnswerChange(value) {
      if (this.submitted || this.isReviewMode) return
      this.$set(this.answers.judge, this.currentNo, value)
      this.$set(this.numberStatus.judge, this.currentNo, value ? 'answered' : 'todo')
      const t = this.questionTypes.find(x => x.key === 'judge')
      if (t) t.done = Object.keys(this.answers.judge || {}).filter(k => this.answers.judge[k]).length
      this.saveDraftState()
      this.persistPaperProgress()
    },
    switchType(type) {
      this.activeType = type
      this.currentNo = 1
      this.saveDraftState()
    },
    selectQuestion(no) {
      this.currentNo = no
      this.saveDraftState()
    },
    prev() {
      if (this.currentNo > 1) {
        this.currentNo--
        this.saveDraftState()
      }
    },
    next() {
      const total = (this.questionTypes.find(x => x.key === this.activeType) || {}).total || 0
      if (this.currentNo < total) {
        this.currentNo++
        this.saveDraftState()
      }
    },

    formatQuestion(q) {
      const content = q.contentCn || q.content_cn || q.contentEn || q.content_en || q.content || ''
      const score = q.score || (this.activeType === 'judge' ? 1 : 2)
      let options = []
      if (this.activeType === 'choice') {
        const optData = q.optionsCn || q.options_cn || q.optionsEn || q.options_en || q.options || ''
        options = this.parseOptions(optData)
      }
      return { id: q.id, score, content, options, optionImages: this.getOptionImages(q), answer: q.answer }
    },

    getQuestionImages(q) {
      if (!q) return []
      const urls = []
      const pushUrl = (value) => {
        const full = this.getImageUrl(value)
        if (full && !urls.includes(full)) urls.push(full)
      }

      const rawPaths = q.imagePaths || q.image_paths
      if (Array.isArray(rawPaths)) {
        rawPaths.forEach(pushUrl)
      } else if (typeof rawPaths === 'string' && rawPaths.trim()) {
        try {
          const parsed = JSON.parse(rawPaths)
          if (Array.isArray(parsed)) {
            parsed.forEach(pushUrl)
          } else if (parsed) {
            pushUrl(parsed)
          }
        } catch (e) {
          rawPaths.split(/[;,]/).map(item => item.trim()).filter(Boolean).forEach(pushUrl)
        }
      }

      const singleImage = q.image || q.url
      if (singleImage) pushUrl(singleImage)
      return urls
    },

    getImageUrl(image) {
      if (!image) return ''
      const s = String(image).trim()
      if (!s) return ''
      if (/^(https?:)?\/\//i.test(s)) return s
      if (s.startsWith('/api/')) return s
      const minioBase = (process.env.VUE_APP_MINIO_URL || '').replace(/\/$/, '')
      if (minioBase) {
        return s.startsWith('/') ? `${minioBase}${s}` : `${minioBase}/${s.replace(/^\/+/, '')}`
      }
      return s
    },

    getOptionImages(q) {
      if (!q) return []
      const images = []
      const pushUrl = (value) => {
        const full = this.getImageUrl(value)
        if (full) images.push(full)
      }
      const rawPaths = q.optionImages || q.option_images
      if (Array.isArray(rawPaths)) {
        rawPaths.forEach(pushUrl)
      } else if (typeof rawPaths === 'string' && rawPaths.trim()) {
        try {
          const parsed = JSON.parse(rawPaths)
          if (Array.isArray(parsed)) {
            parsed.forEach(pushUrl)
          } else if (parsed) {
            pushUrl(parsed)
          }
        } catch (e) {
          rawPaths.split(/[;,]/).map(item => item.trim()).filter(Boolean).forEach(pushUrl)
        }
      }
      return images
    },

    getOptionImage(question, optionIndex) {
      if (!question || !Array.isArray(question.optionImages)) return ''
      return question.optionImages[optionIndex] || ''
    },

    normalizeJudgeAnswer(val) {
      if (!val) return ''
      const v = String(val).trim().toUpperCase()
      if (v === 'A' || /^A\b/.test(v) || v.includes('TRUE') || v.includes('正确') || v.includes('对')) return 'A'
      if (v === 'B' || /^B\b/.test(v) || v.includes('FALSE') || v.includes('错误') || v.includes('错')) return 'B'
      if (v === 'T' || v === '1') return 'A'
      if (v === 'F' || v === '0') return 'B'
      return v
    },

    extractChoiceKey(val) {
      if (val == null) return ''
      const m = String(val).trim().match(/^([A-Ea-e])/)
      return m ? m[1].toUpperCase() : String(val).toUpperCase().trim()
    },

    closeCurrentWindow() {
      try {
        window.open('', '_self')
        window.close()
      } catch (e) {
      }
      return !!window.closed
    },

    closeAndReturn() {
      this.stopTimer()
      this.clearDraftState(this.currentPaperId)
      this.submitResultVisible = false
      const closed = this.closeCurrentWindow()
      if (!closed) {
        this.currentView = 'home'
        this.$router.go(-1)
      }
    },

    submitAll() {
      const judgeAnswered = Object.keys(this.answers.judge || {}).filter(k => this.answers.judge[k]).length
      const choiceAnswered = Object.keys(this.answers.choice || {}).filter(k => this.answers.choice[k]).length
      const codeAnswered = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length
      const totalAnswered = judgeAnswered + choiceAnswered + codeAnswered
      const totalQuestions = this.judgeQuestions.length + this.choiceQuestions.length + this.codeQuestions.length

      if (totalAnswered === 0) {
        this.$message.warning('您还没有作答任何题目')
        return
      }

      const unanswered = totalQuestions - totalAnswered
      const confirmMsg = unanswered > 0
        ? `您还有 ${unanswered} 道题未作答，确定要提交吗？`
        : '确定提交全部答题结果吗？'

      this.$confirm(confirmMsg, '提交确认', {
        confirmButtonText: '确定提交',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          let rightCount = 0
          let wrongCount = 0
          const answerDetailMap = this.buildAnswerDetailMap()

          this.judgeQuestions.forEach((q, idx) => {
            const no = idx + 1
            const userAnswer = this.answers.judge[no]
            if (!userAnswer) return
            const correctAnswer = this.normalizeJudgeAnswer(q.answer)
            const normalizedUser = this.normalizeJudgeAnswer(userAnswer)
            const isCorrect = normalizedUser === correctAnswer
            isCorrect ? rightCount++ : wrongCount++
            const resultEntry = { isCorrect, correctAnswer, userAnswer, score: isCorrect ? 5 : 0 }
            this.$set(this.questionResults, `judge_${no}`, resultEntry)
            this.$set(this.numberStatus.judge, no, isCorrect ? 'done' : 'wrong')
            answerDetailMap.judge[no] = resultEntry
          })

          this.choiceQuestions.forEach((q, idx) => {
            const no = idx + 1
            const userAnswer = this.answers.choice[no]
            if (!userAnswer) return
            const correctAnswer = this.extractChoiceKey(q.answer)
            const normalizedUser = this.extractChoiceKey(userAnswer)
            const isCorrect = normalizedUser === correctAnswer
            isCorrect ? rightCount++ : wrongCount++
            const resultEntry = { isCorrect, correctAnswer, userAnswer, score: isCorrect ? 5 : 0 }
            this.$set(this.questionResults, `choice_${no}`, resultEntry)
            this.$set(this.numberStatus.choice, no, isCorrect ? 'done' : 'wrong')
            answerDetailMap.choice[no] = resultEntry
          })

          answerDetailMap.meta = {
            ...(answerDetailMap.meta || {}),
            elapsedSeconds: Number(this.elapsedSeconds || 0)
          }

          this.submitted = true
          this.stopTimer()
          this.clearDraftState(this.currentPaperId)

          // 更新菜单计数
          const judgeType = this.questionTypes.find(t => t.key === 'judge')
          const choiceType = this.questionTypes.find(t => t.key === 'choice')
          const codeType = this.questionTypes.find(t => t.key === 'code')
          if (judgeType) judgeType.done = Object.values(this.numberStatus.judge || {}).filter(s => s === 'done' || s === 'wrong').length
          if (choiceType) choiceType.done = Object.values(this.numberStatus.choice || {}).filter(s => s === 'done' || s === 'wrong').length
          if (codeType) codeType.done = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length

          const codePassedCount = Object.values(answerDetailMap.code || {}).reduce((sum, item) => sum + (Number(item && item.passedCount) || 0), 0)
          const objectiveCorrectCount = rightCount
          const totalCorrectCount = objectiveCorrectCount + codePassedCount
          const score = (objectiveCorrectCount * 5) + (codePassedCount * 5)
          const totalWrongCount = wrongCount + Math.max(codeAnswered - Object.values(answerDetailMap.code || {}).filter(item => item && item.isCorrect === true).length, 0)
          try {
            if (this.currentPaperId) {
              await submitPaperResult(this.currentPaperId, {
                score,
                correctCount: totalCorrectCount,
                answerDetail: JSON.stringify(answerDetailMap)
              })
            }
          } catch (e) {
            console.warn('提交试卷结果失败:', e)
          }

          if (this.currentPaper) {
            this.currentPaper = {
              ...this.currentPaper,
              answerDetail: JSON.stringify(answerDetailMap),
              correctCount: totalCorrectCount,
              score,
              status: 'completed'
            }
          }

          this.submitResultData = {
            totalAnswered,
            rightCount: totalCorrectCount,
            wrongCount: totalWrongCount,
            totalQuestions
          }
          this.submitResultVisible = true
        } catch (e) {
          console.error('提交失败:', e)
          this.$message.error('提交失败，请稍后重试')
        }
      }).catch(() => {})
    },

    goToCodeDetail(q) {
      const questionIndex = this.codeQuestions.findIndex(item => item && item.id === q.id)
      this.saveDraftState()
      this.$router.push({
        path: '/gesp-code-detail',
        query: {
          questionId: q.id,
          level: this.level || undefined,
          source: 'gesp-mock-test',
          mockPaperId: this.currentPaperId || undefined,
          repoId: this.repoId || undefined,
          codeRepoId: this.codeRepoId || undefined,
          questionNo: questionIndex >= 0 ? questionIndex + 1 : undefined
        }
      })
    },

    difficultyTagType(level) {
      const map = { 1: 'success', 2: 'success', 3: '', 4: '', 5: 'warning', 6: 'danger' }
      return map[level] || ''
    },

    // ========== 计时 ==========
    startTimer() {
      this.stopTimer()
      this.timer = setInterval(() => { this.elapsedSeconds++ }, 1000)
    },
    stopTimer() { if (this.timer) { clearInterval(this.timer); this.timer = null } },
    resetTimer() { this.elapsedSeconds = 0 },

    // ========== 工具 ==========
    formatTime(dt) {
      if (!dt) return ''
      return String(dt).replace('T', ' ').substring(0, 16)
    },

    getPaperElapsedSeconds(paper) {
      if (!paper) return null
      const directElapsed = Number(paper.elapsedSeconds)
      if (Number.isFinite(directElapsed) && directElapsed >= 0) return directElapsed
      return this.getElapsedSecondsFromAnswerDetail(paper.answerDetail)
    },

    formatElapsedForCard(seconds) {
      const total = Number(seconds)
      if (!Number.isFinite(total) || total < 0) return ''
      const hh = Math.floor(total / 3600)
      const mm = Math.floor((total % 3600) / 60)
      const ss = total % 60
      if (hh > 0) {
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
      }
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    },

    parseOptions(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) {
        return raw.map(item => {
          if (typeof item === 'string') {
            const m = item.match(/^\s*\(?([A-Ea-e])\)?[\.\)]?\s*(.*)$/)
            return m ? { key: m[1].toUpperCase(), value: m[2].trim() } : { key: '', value: item }
          }
          if (item && typeof item === 'object') {
            return { key: (item.key || '').toString().toUpperCase(), value: (item.value != null ? String(item.value) : (item.text || '')).trim() }
          }
          return { key: '', value: String(item) }
        }).filter(o => o.key)
      }
      if (typeof raw === 'object') {
        return Object.keys(raw).map(k => ({ key: k.toUpperCase(), value: String(raw[k] || '').trim() }))
      }
      const optionsStr = String(raw)
      const options = []
      const reParen = /\(([A-Ea-e])\)\s*([^;\n\r]+)/g
      let m
      while ((m = reParen.exec(optionsStr)) !== null) {
        options.push({ key: m[1].toUpperCase(), value: m[2].trim() })
      }
      if (options.length) return options
      const parts = optionsStr.split(/\n|;|<br\s*\/>|<br>/i).map(s => s.trim()).filter(Boolean)
      for (const p of parts) {
        const m2 = p.match(/^\s*([A-Ea-e])\s*[\.\)]\s*(.*)$/)
        if (m2) options.push({ key: m2[1].toUpperCase(), value: m2[2].trim() })
      }
      return options
    }
  }
}
</script>

<style scoped>
.gesp-mock-container { min-height: 100vh; background: #f5f7fa; display: flex; flex-direction: column; }
.gesp-header { background: #fff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); position: sticky; top: 0; z-index: 10; }
.header-left { display: flex; align-items: center; gap: 10px; }
.header-right { display: flex; align-items: center; gap: 10px; }
.back-btn { font-size: 18px; color: #666; cursor: pointer; padding: 4px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 16px; font-weight: 600; }
.timer-bar { display: flex; align-items: center; gap: 6px; color: #409EFF; font-weight: 600; }

/* ===== 首页 ===== */
.home-view { padding: 20px; max-width: 960px; margin: 0 auto; width: 100%; }
.loading-container, .empty-container { display: flex; align-items: center; justify-content: center; min-height: 200px; color: #666; gap: 8px; flex-direction: column; }
.mode-tabs { display: flex; gap: 0; margin-bottom: 24px; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.mode-tab { flex: 1; padding: 16px; text-align: center; cursor: pointer; font-size: 15px; font-weight: 600; color: #666; transition: all 0.3s; border-bottom: 3px solid transparent; }
.mode-tab:hover { background: #f8f9fa; }
.mode-tab.active { color: #409eff; border-bottom-color: #409eff; background: #ecf5ff; }
.mode-tab i { margin-right: 6px; }

/* 试卷网格 */
.paper-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
.paper-card { display: flex; align-items: center; gap: 16px; background: #fff; border-radius: 12px; padding: 16px 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); cursor: pointer; transition: all 0.3s; border: 2px solid transparent; }
.paper-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-color: #409eff; }
.paper-card.completed { border-color: #67c23a; }
.paper-card-icon { width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; flex-shrink: 0; }
.paper-card.completed .paper-card-icon { background: linear-gradient(135deg, #67c23a 0%, #4caf50 100%); }
.paper-card-info { flex: 1; }
.paper-card-title { font-size: 15px; font-weight: 600; color: #333; margin-bottom: 4px; }
.paper-card-meta { font-size: 13px; color: #999; display: flex; gap: 8px; align-items: center; }
.paper-card-time { font-size: 12px; color: #bbb; margin-top: 2px; }
.score-badge { background: #f0f9ff; color: #409eff; padding: 1px 6px; border-radius: 4px; font-size: 12px; }
.paper-card-actions { display: flex; gap: 6px; align-items: center; }
.paper-card-btn { flex-shrink: 0; }

/* 空状态 */
.empty-hint { text-align: center; padding: 40px 20px; color: #999; }

/* 组卷配置 */
.custom-form-card { background: #fff; border-radius: 12px; padding: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 24px; }
.form-title { margin: 0 0 16px; font-size: 16px; color: #333; }
.form-title i { margin-right: 6px; color: #409eff; }
.diff-sliders { display: flex; flex-direction: column; gap: 8px; }
.diff-row { display: flex; align-items: center; gap: 12px; }
.diff-label { width: 56px; font-size: 13px; font-weight: 600; text-align: right; }
.diff-tip { margin-top: 8px; color: #909399; font-size: 12px; line-height: 1.6; }
.diff-actions { margin-top: 8px; }
.diff-val { width: 40px; text-align: right; font-size: 13px; color: #666; }
.judge-label { color: #409eff; }
.choice-label { color: #67c23a; }
.code-label { color: #e6a23c; }

/* 历史记录 */
.history-section { margin-top: 0; }
.section-title { font-size: 16px; color: #333; margin: 0 0 12px; }
.section-title i { margin-right: 6px; color: #909399; }

/* Practice view - same layout as GESPPractice */
.practice-view { flex: 1; display: flex; flex-direction: column; }
.gesp-body { display: flex; gap: 12px; padding: 12px; flex: 1; min-height: 0; align-items: stretch; }
.left-panel { width: 300px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; display: flex; flex-direction: column; }
.right-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.panel-title { font-weight: 600; color: #333; margin-bottom: 10px; }

.type-menu { display: flex; flex-direction: column; gap: 6px; }
.type-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-radius: 8px; cursor: pointer; color: #333; background: #f8fafc; border: 1px solid #eef2f7; }
.type-item:hover { background: #f3f6fb; }
.type-item.active { border-color: #409EFF; background: #ecf5ff; }
.type-name { font-weight: 600; }
.type-count { color: #666; font-size: 12px; }

.number-panel { margin-top: 12px; }
.number-title { display: flex; justify-content: space-between; align-items: center; font-weight: 600; color: #333; margin-bottom: 8px; }
.legend { display: flex; flex-wrap: wrap; gap: 8px 12px; margin-bottom: 10px; color: #666; font-size: 12px; }
.legend-item { display: flex; align-items: center; gap: 6px; }
.dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.dot.done { background: #67C23A; }
.dot.todo { background: #C0C4CC; }
.dot.wrong { background: #F56C6C; }
.dot.answered { background: #409EFF; }

.number-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.num { height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; border: 1px solid #e5e7eb; color: #333; background: #fff; font-size: 13px; }
.num.todo { background: #fff; }
.num.done { background: #f0f9eb; border-color: #67c23a; color: #2f7a1f; }
.num.wrong { background: #fef0f0; border-color: #f8d4d4; color: #b33a3a; }
.num.answered { background: #f0f9eb; border-color: #67c23a; color: #2f7a1f; }

.question-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 24px 32px; flex: 1; display: flex; flex-direction: column; }
.q-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid #f0f0f0; }
.q-title { display: flex; align-items: center; gap: 8px; }
.q-id { font-weight: 700; color: #333; font-size: 16px; }
.q-meta { color: #666; font-size: 13px; }
.q-content { line-height: 1.8; color: #333; margin-bottom: 24px; font-size: 16px; }
.q-images { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.q-image { width: 100%; max-width: 520px; border-radius: 8px; border: 1px solid #ebeef5; background: #fff; }
.q-options { margin-bottom: 24px; font-size: 15px; }
.opt { display: block; margin: 10px 0; padding: 4px 0; }
.opt-image-wrap { display: flex; align-items: flex-start; gap: 8px; }
.opt-key { min-width: 20px; font-weight: 600; color: #333; line-height: 24px; }
.opt-image { width: 100%; max-width: 320px; max-height: 240px; border-radius: 8px; border: 1px solid #ebeef5; background: #fff; }
.q-actions { display: flex; gap: 10px; margin-top: 24px; padding-top: 16px; border-top: 1px solid #f0f0f0; }

.q-result { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 8px; padding: 14px; margin-top: 20px; }
.result-row { display: flex; justify-content: space-between; padding: 6px 0; }
.result-label { color: #666; font-weight: 500; }
.result-value { font-weight: 600; }
.result-value.correct { color: #67C23A; }
.result-value.wrong { color: #F56C6C; }

.code-list-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border: 1px solid #eef2f7; border-radius: 8px; margin-top: 10px; background: #fafbfc; }
.code-list-item.completed { border-color: #67c23a; }
.code-list-item:hover { background: #f0f7ff; border-color: #c6e2ff; }
.code-list-item.completed:hover { border-color: #67c23a; }
.code-list-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.code-list-no { width: 28px; height: 28px; border-radius: 50%; background: #409EFF; color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.code-list-item.completed .code-list-no { background: #67c23a; }
.code-list-info { min-width: 0; }
.code-list-number { font-size: 12px; color: #999; }
.code-list-title { font-weight: 600; color: #333; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.code-list-right { display: flex; align-items: center; flex-shrink: 0; }
</style>
